import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import profile from "../images/profile.jpg";
import axios from "../axiosConfig";
import { BASE_SERVER_URL } from "../sampleData";
import { useAuth } from "../auth/AuthContext";
import { roleLabel } from "../auth/permissions";
import { clearActiveBusinessId } from "../auth/tokenStore";
import "../overview.css";
import "../group-products.css";

/* ------------------------------------------------------------------ *
 * Group directory of a shareable collection (products, funeral
 * directors): every row across the group, one business at a time, plus
 * the "Global" group — rows marked `shared`, which every business can see
 * and use. Reached from the overview cards. Sits outside any business
 * (same standalone chrome as /overview).
 *
 * DATA — GET <fetchUrl>
 *   { success, task: 'group', data: { businesses: [{ business_id, name,
 *     code, can_write, <countsKey>: { total, shared } }], total,
 *     shared_total, <listKey>: [{ _id, shared, business_id, ... }] } }
 *
 * WRITES go to POST <bulkUrl> { action, ids } with an explicit
 * `business-id` header naming the business whose rows are being changed.
 * A shared row may be changed from any business (the server scopes to
 * "mine OR shared"), so for the Global group the header names any
 * business the caller can write in.
 * ------------------------------------------------------------------ */

const GLOBAL = "global";
const inBusiness = (bizId) => ({ headers: { "business-id": String(bizId) } });

export default function GroupSharedDirectory({
  pageTitle,   // topbar, e.g. "Group catalogue"
  heading,     // h1
  noun,        // "product"
  nounPlural,  // "products"
  fetchUrl,    // "/group/products"
  listKey,     // "products"
  countsKey,   // "products"
  bulkUrl,     // "/products/bulk"
  searchFields,
  searchPlaceholder,
  renderItem,  // (item, { isGlobal, ownerName }) => card contents
  gridClass,   // extra class on the grid, e.g. "gp-grid-fd"
}) {
  const auth = useAuth() || {};
  const user = auth.user || {};
  const navigate = useNavigate();

  useEffect(() => {
    clearActiveBusinessId();
  }, []);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(null); // business_id | GLOBAL
  const [selected, setSelected] = useState(() => new Set());
  const [query, setQuery] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    axios
      .get(`${BASE_SERVER_URL}${fetchUrl}`)
      .then((res) => {
        const d = res && res.data && res.data.data;
        if (!d) throw new Error("Malformed response");
        setData(d);
      })
      .catch((err) => {
        setData(null);
        setError(
          (err && err.response && err.response.data && err.response.data.message) ||
            "The server didn't answer."
        );
      })
      .finally(() => setLoading(false));
  }, [fetchUrl]);

  useEffect(() => {
    load();
  }, [load]);

  const items = useMemo(() => (data && data[listKey]) || [], [data, listKey]);

  /* One tab per business (its private rows), then Global. */
  const groups = useMemo(() => {
    if (!data) return [];
    const list = (data.businesses || []).map((b) => {
      const c = b[countsKey] || { total: 0, shared: 0 };
      return {
        key: String(b.business_id),
        label: b.name,
        count: c.total - c.shared,
        canWrite: Boolean(b.can_write),
        writeAs: String(b.business_id),
      };
    });
    const writable = list.find((g) => g.canWrite);
    list.push({
      key: GLOBAL,
      label: "Global",
      count: data.shared_total || 0,
      canWrite: Boolean(writable),
      writeAs: writable ? writable.writeAs : null,
    });
    return list;
  }, [data, countsKey]);

  useEffect(() => {
    if (groups.length && !groups.some((g) => g.key === active)) setActive(groups[0].key);
  }, [groups, active]);

  const group = groups.find((g) => g.key === active) || null;
  const isGlobal = Boolean(group && group.key === GLOBAL);

  const ownerName = useMemo(() => {
    const m = new Map((data ? data.businesses : []).map((b) => [String(b.business_id), b]));
    return (id) => (m.get(String(id)) || {}).name || "Unknown";
  }, [data]);

  const visible = useMemo(() => {
    if (!group) return [];
    const q = query.trim().toLowerCase();
    return items.filter((it) => {
      const inGroup = isGlobal ? it.shared : !it.shared && it.business_id === group.key;
      if (!inGroup) return false;
      if (!q) return true;
      return searchFields.some((f) => it[f] && String(it[f]).toLowerCase().includes(q));
    });
  }, [items, group, isGlobal, query, searchFields]);

  const switchGroup = (key) => {
    setActive(key);
    setSelected(new Set());
    setConfirming(false);
  };

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allVisibleSelected = visible.length > 0 && visible.every((it) => selected.has(it._id));
  const toggleAll = () =>
    setSelected(allVisibleSelected ? new Set() : new Set(visible.map((it) => it._id)));

  const label = (n) => `${n} ${n === 1 ? noun : nounPlural}`;

  async function run(action) {
    if (!group || !group.writeAs || selected.size === 0) return;
    setBusy(true);
    try {
      const res = await axios.post(
        `${BASE_SERVER_URL}${bulkUrl}`,
        { action, ids: Array.from(selected) },
        inBusiness(group.writeAs)
      );
      const n = (res.data && res.data.response && res.data.response.count) || 0;
      if (action === "delete") toast.success(`Deleted ${label(n)}.`);
      else if (action === "share") toast.success(`${label(n)} now global.`);
      else toast.success(`${label(n)} back to private.`);
      setSelected(new Set());
      setConfirming(false);
      load();
    } catch (err) {
      toast.error(
        (err && err.response && err.response.data && err.response.data.message) ||
          "That didn't go through."
      );
    } finally {
      setBusy(false);
    }
  }

  async function handleLogout() {
    if (auth.logout) await auth.logout();
  }

  const displayName = user.full_name || user.userName || "—";
  const lede = !data
    ? `Every ${noun} across the group.`
    : `${label(data.total)} across ${data.businesses.length} business${
        data.businesses.length === 1 ? "" : "es"
      }, ${data.shared_total} of them global. Pick a group, tick ${nounPlural}, then make them global or delete them.`;

  return (
    <div className="overview-page">
      <div className="ov-topbar">
        <div className="ov-topbar-brand">
          <button
            type="button"
            className="ov-btn ov-btn-ghost gp-topbar-back"
            onClick={() => navigate("/overview")}
          >
            ‹ Overview
          </button>
          <span className="ov-rule-v" />
          <span className="ov-serif ov-topbar-title">{pageTitle}</span>
        </div>
        <span className="ov-spacer" />
        <div className="ov-user-wrap">
          <div className="ov-user" style={{ cursor: "default" }}>
            <img src={profile} alt="" />
            <div>
              <div className="ov-user-name">{displayName}</div>
              <div className="ov-user-role">{roleLabel(user)}</div>
            </div>
          </div>
        </div>
        <button type="button" className="ov-btn ov-btn-ghost" onClick={handleLogout}>
          Log out
        </button>
      </div>

      <div className="ov-body gp-body">
        <div className="gp-head">
          <div>
            <h1 className="ov-serif gp-title">{heading}</h1>
            <p className="gp-lede">{lede}</p>
          </div>
        </div>

        {error && (
          <div className="gp-panel gp-error">
            <span>{error}</span>
            <button type="button" className="ov-btn ov-btn-ghost" onClick={load}>
              Retry
            </button>
          </div>
        )}

        {!error && (
          <div className="ov-dir-card gp-card">
            <div className="gp-toolbar">
              <div className="ov-segmented gp-groups">
                {groups.map((g) => (
                  <button
                    key={g.key}
                    type="button"
                    className={g.key === active ? "is-active" : undefined}
                    onClick={() => switchGroup(g.key)}
                  >
                    {g.key === GLOBAL && <span className="gp-globe">◎</span>}
                    {g.label}
                    <span className="gp-count">{g.count}</span>
                  </button>
                ))}
              </div>
              <span className="ov-spacer" />
              <input
                className="gp-search"
                type="search"
                placeholder={searchPlaceholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>

            <div className={`gp-actions${selected.size ? " has-selection" : ""}`}>
              <label className="rf-check gp-check">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAll}
                  disabled={visible.length === 0}
                />
                <span className="rf-check-box">✓</span>
                <span className="rf-check-label">
                  {selected.size
                    ? `${selected.size} selected`
                    : `Select all ${visible.length ? `(${visible.length})` : ""}`}
                </span>
              </label>
              <span className="ov-spacer" />
              {group && !group.canWrite && (
                <span className="gp-readonly">View only in this group</span>
              )}
              {group && group.canWrite && !confirming && (
                <>
                  <button
                    type="button"
                    className="ov-btn ov-btn-primary"
                    disabled={busy || selected.size === 0}
                    onClick={() => run(isGlobal ? "unshare" : "share")}
                  >
                    {isGlobal ? "Make private again" : `Make ${nounPlural} global`}
                  </button>
                  <button
                    type="button"
                    className="ov-btn gp-btn-danger"
                    disabled={busy || selected.size === 0}
                    onClick={() => setConfirming(true)}
                  >
                    Delete
                  </button>
                </>
              )}
              {group && group.canWrite && confirming && (
                <span className="gp-confirm">
                  Delete {label(selected.size)}? This can't be undone.
                  <button
                    type="button"
                    className="ov-btn ov-btn-ghost"
                    disabled={busy}
                    onClick={() => setConfirming(false)}
                  >
                    Keep
                  </button>
                  <button
                    type="button"
                    className="ov-btn gp-btn-danger"
                    disabled={busy}
                    onClick={() => run("delete")}
                  >
                    {busy ? "Deleting…" : "Yes, delete"}
                  </button>
                </span>
              )}
            </div>

            {loading && !data && <div className="gp-empty">Loading…</div>}

            {data && visible.length === 0 && (
              <div className="gp-empty">
                {query
                  ? "Nothing matches that search."
                  : isGlobal
                  ? `No global ${nounPlural} yet. Pick a business, tick some ${nounPlural} and make them global.`
                  : `No private ${nounPlural} in this business.`}
              </div>
            )}

            {visible.length > 0 && (
              <div className={`gp-grid${gridClass ? ` ${gridClass}` : ""}`}>
                {visible.map((it) => {
                  const on = selected.has(it._id);
                  return (
                    <label key={it._id} className={`gp-item${on ? " is-on" : ""}`}>
                      <input type="checkbox" checked={on} onChange={() => toggle(it._id)} />
                      <span className="gp-tick">✓</span>
                      {renderItem(it, { isGlobal, ownerName: ownerName(it.business_id) })}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
